import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, Tag, AlertCircle, AlertTriangle, CheckCircle2, XCircle, Clock, ArrowRight, Info, Check } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { useAppData } from '../../contexts/AppDataContext';
import { Badge } from '../SubscriptionAppBadge';
import { Modal } from './components/Modal';
import { Calendar } from './components/Calendar';
import { ProductSwapModal, Product } from './components/ProductSwapModal';
export type BulkActionType = 'reschedule' | 'swap' | 'status' | 'preview' | 'submitted' | null;
/**
 * Charge time of day is a global account setting (Settings > General), not a
 * per-action choice. The bulk reschedule flow therefore collects a DATE only
 * and shows the account's billing time as read-only context so the merchant
 * still knows when the charge will land.
 */
const GLOBAL_BILLING_TIME_LABEL = '10:00 AM';
const NOTIFY_MESSAGE_MAX = 300;
export interface BulkActionsManagerProps {
  selectedIds: string[];
  onClearSelection: () => void;
  onActionComplete: () => void;
  /** Read-only label for the account-wide charge time, e.g. "10:00 AM". */
  billingTimeLabel?: string;
  className?: string;
}
// Mock wine product data
const mockProducts: Product[] = [{
  id: '1',
  name: 'Château Margaux 2015',
  description: 'Elegant Bordeaux with notes of blackcurrant and cedar',
  price: 450.0,
  billingPeriod: 'bottle',
  features: ['Bordeaux, France', 'Cabernet Sauvignon', 'Vintage 2015', '14.5% ABV'],
  category: 'red'
}, {
  id: '2',
  name: 'Cloudy Bay Sauvignon Blanc',
  description: 'Crisp New Zealand white with tropical fruit notes',
  price: 32.0,
  billingPeriod: 'bottle',
  features: ['Marlborough, NZ', 'Sauvignon Blanc', 'Vintage 2022', '13% ABV'],
  category: 'white'
}, {
  id: '3',
  name: 'Barolo Riserva DOCG',
  description: 'Bold Italian red with cherry and truffle complexity',
  price: 85.0,
  billingPeriod: 'bottle',
  features: ['Piedmont, Italy', 'Nebbiolo', 'Vintage 2016', '14% ABV'],
  category: 'red'
}, {
  id: '4',
  name: 'Moët & Chandon Impérial',
  description: 'Classic Champagne with apple and brioche notes',
  price: 55.0,
  billingPeriod: 'bottle',
  features: ['Champagne, France', 'Chardonnay Blend', '12% ABV'],
  category: 'sparkling'
}, {
  id: '5',
  name: 'Caymus Cabernet Sauvignon',
  description: 'Rich Napa Valley red with dark fruit and vanilla',
  price: 90.0,
  billingPeriod: 'bottle',
  features: ['Napa Valley, CA', 'Cabernet Sauvignon', 'Vintage 2020', '14.8% ABV'],
  category: 'red'
}, {
  id: '6',
  name: 'Chablis Premier Cru',
  description: 'Mineral-driven white Burgundy with citrus notes',
  price: 48.0,
  billingPeriod: 'bottle',
  features: ['Burgundy, France', 'Chardonnay', 'Vintage 2021', '12.5% ABV'],
  category: 'white'
}];
export const BulkActionsManager = ({
  selectedIds,
  onClearSelection,
  onActionComplete,
  billingTimeLabel = GLOBAL_BILLING_TIME_LABEL,
  className = ''
}: BulkActionsManagerProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentAction, setCurrentAction] = useState<BulkActionType>(null);
  // Action States
  const [newDate, setNewDate] = useState<Date | undefined>(undefined);
  // Reschedule either to a picked date or back onto each member's pack schedule
  const [rescheduleMode, setRescheduleMode] = useState<'pickDate' | 'putBackOnSchedule'>('pickDate');
  const {
    subscriptions
  } = useAppData();
  // Selected members on a pack with fixed billing dates (fixed day or release dates)
  const syncedCount = subscriptions.filter(sub => selectedIds.includes(sub.id) && sub.billingSchedule !== undefined && sub.billingSchedule !== 'rolling').length;
  const isPutBack = rescheduleMode === 'putBackOnSchedule';
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [replacementProduct, setReplacementProduct] = useState<string>('');
  const [newStatus, setNewStatus] = useState<string>('');
  const [notifySubscribers, setNotifySubscribers] = useState<boolean>(true);
  const [notifyMessage, setNotifyMessage] = useState<string>('');
  /** Frozen at submit time so the confirmation reads correctly regardless of selection state. */
  const [submittedSummary, setSubmittedSummary] = useState<{
    count: number;
    actionLabel: string;
  } | null>(null);
  // Today at midnight — used to stop past dates being selected.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const statusLabels: Record<string, string> = {
    active: 'Active',
    paused: 'Paused',
    cancelled: 'Cancelled'
  };
  // Handlers
  const openAction = (action: BulkActionType) => {
    setCurrentAction(action);
    // Reset states
    setNewDate(undefined);
    setRescheduleMode('pickDate');
    setSelectedProduct('');
    setReplacementProduct('');
    setNewStatus('');
    setNotifySubscribers(true);
    setNotifyMessage('');
  };
  const handleConfirmAction = () => {
    setCurrentAction('preview');
  };
  const handleProductSwapConfirm = () => {
    // Move to preview when both products are selected
    setCurrentAction('preview');
  };
  /**
   * Queues the job and shows the confirmation.
   *
   * IMPORTANT: the selection is deliberately NOT cleared here. This component
   * is rendered by the parent table only while rows are selected, so clearing
   * at submit time unmounts the component and the confirmation never appears.
   * Both onActionComplete and onClearSelection are deferred to the moment the
   * merchant dismisses the confirmation.
   */
  const handleFinalUpdate = () => {
    // Here you would make the API call that queues the background job
    const actionLabel = isPutBack ? 'put back on schedule' : newDate ? 'reschedule' : selectedProduct && replacementProduct ? 'product swap' : 'status update';
    setSubmittedSummary({
      count: selectedIds.length,
      actionLabel
    });
    setCurrentAction('submitted');
  };
  const finishSubmitted = () => {
    setCurrentAction(null);
    setSubmittedSummary(null);
    onActionComplete();
    onClearSelection();
  };
  const handleViewJobs = () => {
    // Switch the Subscription Dashboard to its Bulk Update Jobs tab, then clear.
    navigate(location.pathname, {
      state: {
        activeTab: 'jobs'
      }
    });
    finishSubmitted();
  };
  const formatLongDate = (date: Date) => date.toLocaleDateString('en-AU', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  // Render Helpers
  const rescheduleOptions = [{
    id: 'pickDate' as const,
    title: 'Pick a date',
    description: 'Move every selected subscription to the same new charge date.',
    disabled: false
  }, {
    id: 'putBackOnSchedule' as const,
    title: 'Put back on schedule',
    description: syncedCount > 0 ? `Moves ${syncedCount} ${syncedCount === 1 ? 'member' : 'members'} on a fixed schedule to their pack's next scheduled date. No date to pick.` : 'None of the selected members are on a pack with fixed billing dates.',
    disabled: syncedCount === 0
  }];
  const renderRescheduleContent = () => <div className="flex flex-col items-center" data-id="element-437" data-mp="1ss0eulzzq4nq">
      <p className="text-[#475569] mb-6 text-center" data-id="element-438" data-mp="26lrorvhmvqjz">
        Change the next charge date for{' '}
        <strong data-id="element-439" data-mp="21ujdsv0nnx4p">{selectedIds.length}</strong>{' '}
        {selectedIds.length === 1 ? 'subscription' : 'subscriptions'}.
      </p>
      <div className="w-full space-y-2 mb-6" role="radiogroup" aria-label="How to reschedule" data-id="element-440" data-mp="2gi4igtd6i4eq">
        {rescheduleOptions.map(option => <button key={option.id} type="button" role="radio" aria-checked={rescheduleMode === option.id} disabled={option.disabled} onClick={() => setRescheduleMode(option.id)} className={`w-full p-4 rounded-lg border text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed
              ${rescheduleMode === option.id ? 'border-[#0EA5E9] bg-[#F0F9FF] ring-1 ring-[#0EA5E9]' : 'border-[#E2E8F0] hover:border-[#CBD5E1] hover:bg-[#F8FAFC]'}
            `} data-id="element-441" data-mp="2bmyvdr81ijp2">
            <span className="block font-medium text-[#0F172A]" data-id="element-442" data-mp="1fp67259hmq6d">
              {option.title}
            </span>
            <span className="block text-[13px] text-[#64748B] mt-0.5" data-id="element-443" data-mp="1kmo9rdwww95x">
              {option.description}
            </span>
          </button>)}
      </div>
      {!isPutBack && <div className="border border-[#E2E8F0] rounded-lg p-4 bg-white shadow-sm" data-id="element-444" data-mp="1tfsx7916n7qu">
          <Calendar value={newDate} onChange={setNewDate} minDate={today} data-id="element-445" data-mp="wrlgmtr2rjjn" />
        </div>}
      {!isPutBack && newDate && <div className="mt-4 p-4 bg-[#E0F2FE] rounded-lg w-full" data-id="element-446" data-mp="11qprdotndpc3">
          <p className="text-[14px] text-[#475569] mb-1" data-id="element-447" data-mp="15r09fzups67b">New Charge Date:</p>
          <p className="text-[16px] font-medium text-[#0F172A]" data-id="element-448" data-mp="15tupzgalx6vg">
            {formatLongDate(newDate)}
          </p>
        </div>}
      <div className="mt-4 flex items-start gap-2 text-[13px] text-[#64748B]" data-id="element-449" data-mp="2h76snnumrjg1">
        <Info size={16} className="shrink-0 mt-[2px] text-[#94A3B8]" data-id="element-450" data-mp="2m5hyzolpvbti" />
        <span data-id="element-451" data-mp="2m7urgvp3p4kt">
          Charges are taken at {billingTimeLabel}, set by your account's billing
          time in Settings.
        </span>
      </div>
    </div>;
  const renderStatusContent = () => <div className="space-y-2" data-id="element-452" data-mp="2rbdmt5u3lfjy">
      <p className="text-[#475569] mb-4" data-id="element-453" data-mp="2mjvjwc8il4wc">
        Change status for <strong data-id="element-454" data-mp="2mi2f72hgouqz">{selectedIds.length}</strong>{' '}
        {selectedIds.length === 1 ? 'subscription' : 'subscriptions'}.
      </p>
      {[{
      value: 'active',
      label: 'Active',
      icon: CheckCircle2,
      color: 'text-green-600'
    }, {
      value: 'paused',
      label: 'Paused',
      icon: Clock,
      color: 'text-orange-500'
    }, {
      value: 'cancelled',
      label: 'Cancelled',
      icon: XCircle,
      color: 'text-red-500'
    }].map(status => <button key={status.value} onClick={() => setNewStatus(status.value)} className={`w-full p-4 rounded-lg border text-left flex items-center gap-3 transition-all
            ${newStatus === status.value ? 'border-[#0EA5E9] bg-[#F0F9FF] ring-1 ring-[#0EA5E9]' : 'border-[#E2E8F0] hover:border-[#CBD5E1] hover:bg-[#F8FAFC]'}
          `} data-id="element-455" data-mp="1zkh9y581fawo">
          <status.icon className={status.color} size={20} data-id="element-456" data-mp="32ng3fgtdsx8s" />
          <span className="font-medium text-[#0F172A]" data-id="element-457" data-mp="2yj01trqswbzd">{status.label}</span>
        </button>)}
    </div>;
  const renderPreviewContent = () => {
    const selectedProductName = mockProducts.find(p => p.id === selectedProduct)?.name;
    const replacementProductName = mockProducts.find(p => p.id === replacementProduct)?.name;
    const isSwap = Boolean(selectedProduct && replacementProduct);
    return <div className="space-y-6" data-id="element-458" data-mp="2ewu1m79z0978">
        <div className="bg-[#F0F9FF] border border-[#BAE6FD] rounded-lg p-4 flex gap-3" data-id="element-459" data-mp="2etpo2jam813w">
          <AlertCircle className="text-[#0284C7] shrink-0" size={24} data-id="element-460" data-mp="2tlzxbl9lnhad" />
          <div data-id="element-461" data-mp="2osx9ltri7223">
            <h4 className="font-semibold text-[#0C4A6E]" data-id="element-462" data-mp="2osduekt8mrrr">
              You are about to update {selectedIds.length}{' '}
              {selectedIds.length === 1 ? 'subscription' : 'subscriptions'}
            </h4>
            <p className="text-[#0284C7] text-sm mt-1" data-id="element-463" data-mp="1w36vhkpvg05z">
              Please review the changes below before confirming.
            </p>
          </div>
        </div>

        {newDate && !isPutBack && syncedCount > 0 && <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-4 flex gap-3" role="alert" data-id="element-464" data-mp="gftk6vpx6dfl">
            <AlertTriangle className="text-[#D97706] shrink-0" size={20} data-id="element-465" data-mp="2kr79omnnnta" />
            <p className="text-[#92400E] text-[14px]" data-id="element-466" data-mp="6ofbpyfzq3ab">
              {syncedCount} of these members{' '}
              {syncedCount === 1 ? 'is' : 'are'} on a release schedule. This
              date moves them off it.
            </p>
          </div>}

        <div className="border border-[#E2E8F0] rounded-lg divide-y divide-[#E2E8F0]" data-id="element-467" data-mp="ldlaxmetvti7">
          <div className="p-4 flex justify-between items-center" data-id="element-468" data-mp="qbwkdw17o1zw">
            <span className="text-[#64748B]" data-id="element-469" data-mp="qe99qu9atemz">Action Type</span>
            <Badge variant="info" data-id="element-470" data-mp="3629epuyimst6">
              {isPutBack ? 'Put back on schedule' : newDate ? 'Reschedule' : isSwap ? 'Swap Product' : 'Update Status'}
            </Badge>
          </div>
          <div className="p-4" data-id="element-471" data-mp="1gmf769mlz9lt">
            <span className="text-[#64748B] block mb-2" data-id="element-472" data-mp="1gldrfrundyxr">
              {isPutBack || newDate ? 'New Charge Date' : isSwap ? 'Product Change' : 'New Status'}
            </span>
            {isPutBack ? <div data-id="element-473" data-mp="16usv6b2g56i0">
                <span className="font-medium text-[#0F172A] block" data-id="element-474" data-mp="16u9baot8k81c">
                  Each member's next scheduled pack date
                </span>
                <span className="text-[13px] text-[#64748B]" data-id="element-475" data-mp="xvxokxvhpue6">
                  {syncedCount} {syncedCount === 1 ? 'member moves' : 'members move'}{' '}
                  back onto their pack's schedule.
                  {selectedIds.length > syncedCount ? ` ${selectedIds.length - syncedCount} on rolling billing ${selectedIds.length - syncedCount === 1 ? "isn't" : "aren't"} changed.` : ''}
                </span>
              </div> : newDate ? <div data-id="element-476" data-mp="2iazf61a6snrs">
                <span className="font-medium text-[#0F172A] block" data-id="element-477" data-mp="2i96gp99q99uv">
                  {formatLongDate(newDate)}
                </span>
                <span className="text-[13px] text-[#64748B]" data-id="element-478" data-mp="2ocs81e25fr">
                  Charged at {billingTimeLabel}, per your account's billing time
                </span>
              </div> : isSwap ? <div className="flex items-center gap-2 text-[#0F172A]" data-id="element-479" data-mp="9s7v9cl6vxa3">
                <span className="font-medium" data-id="element-480" data-mp="9u2hcu875vfr">{selectedProductName}</span>
                <ArrowRight size={16} className="text-[#0EA5E9]" data-id="element-481" data-mp="ds9pj0olhofc" />
                <span className="font-medium" data-id="element-482" data-mp="3d97urp10yzm8">{replacementProductName}</span>
              </div> : <span className="font-medium text-[#0F172A]" data-id="element-483" data-mp="3i7htg9lykicl">
                {statusLabels[newStatus] ?? newStatus}
              </span>}
          </div>
          <div className="p-4" data-id="element-484" data-mp="3m7tcjhyoaaly">
            <div className="flex justify-between items-center gap-4" data-id="element-485" data-mp="3mbpjqb82e6cx">
              <div data-id="element-486" data-mp="3ra0r8faw04rz">
                <span className="text-[#0F172A] font-medium block mb-1" data-id="element-487" data-mp="3r637pskedb7k">
                  Notify affected subscribers
                </span>
                <span className="text-[#64748B] text-sm" data-id="element-488" data-mp="16hfjisd70khp">
                  Send an email to each member about this change
                </span>
              </div>
              <button onClick={() => setNotifySubscribers(!notifySubscribers)} role="switch" aria-checked={notifySubscribers} aria-label="Notify affected subscribers" className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${notifySubscribers ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-489" data-mp="152w8x4518nd1">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notifySubscribers ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-490" data-mp="1nzdz3h48gf76" />
              </button>
            </div>

            {/* Message to members — same pattern as Product Replace and Price Sync */}
            {notifySubscribers && <div className="mt-4" data-id="element-491" data-mp="1srozel2vy309">
                <label htmlFor="bulk-notify-message" className="block text-[14px] font-medium text-[#0F172A] mb-1" data-id="element-492" data-mp="1ssqnbq502jze">
                  Message to members{' '}
                  <span className="font-normal text-[#94A3B8]" data-id="element-493" data-mp="21sddtgw6y5do">(optional)</span>
                </label>
                <p className="text-[13px] text-[#64748B] mb-2" data-id="element-494" data-mp="2le5jj6i5sutm">
                  This is the first thing the member reads, so it's worth saying
                  why. It comes through to your email as a merge field.
                </p>
                <textarea id="bulk-notify-message" value={notifyMessage} onChange={event => setNotifyMessage(event.target.value.slice(0, NOTIFY_MESSAGE_MAX))} rows={3} placeholder="Harvest has run late this year, so we've moved your next delivery back a few weeks." className="w-full px-3 py-2 text-[14px] text-[#0F172A] bg-white border border-[#CBD5E1] rounded-md placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 transition-all duration-200 resize-y" data-id="element-495" data-mp="2ceifse1yjcxe" />
                <div className="flex justify-between items-center mt-1" data-id="element-496" data-mp="1jo0eniksnlmm">
                  <span className="text-[12px] text-[#94A3B8]" data-id="element-497" data-mp="1jpuxmrci93o2">
                    Leave blank to send the email without a message.
                  </span>
                  <span className="text-[12px] text-[#94A3B8]" data-id="element-498" data-mp="3qd2bw2scy4zs">
                    {notifyMessage.length}/{NOTIFY_MESSAGE_MAX}
                  </span>
                </div>
              </div>}

            {!notifySubscribers && <p className="mt-3 text-[13px] text-[#64748B]" data-id="element-499" data-mp="3gmqwnk3849dl">
                Members won't be told about this change, and no flow built on it
                will run.
              </p>}
          </div>
        </div>

        {newStatus === 'cancelled' && <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-4 flex gap-3" data-id="element-500" data-mp="86nprgjxqoqb">
            <XCircle className="text-[#DC2626] shrink-0" size={20} data-id="element-501" data-mp="44sqehlkelf6" />
            <div data-id="element-502" data-mp="42zikyz7tv5l">
              <h4 className="font-semibold text-[#991B1B] text-[14px]" data-id="element-503" data-mp="46nc3y4nuhkb">
                Cancelling can't be undone
              </h4>
              <p className="text-[#B91C1C] text-[13px] mt-1" data-id="element-504" data-mp="2ovkwj3ni6bob">
                These {selectedIds.length} members will need to be set up again
                from scratch if you change your mind.
              </p>
            </div>
          </div>}
      </div>;
  };
  const renderSubmittedContent = () => <div className="flex flex-col items-center text-center py-2" data-id="element-505" data-mp="14cheew9aj0f1">
      <div className="w-14 h-14 rounded-full bg-[#D1FAE5] flex items-center justify-center mb-4" data-id="element-506" data-mp="14ampl6uweo5o">
        <Check className="text-[#059669]" size={28} data-id="element-507" data-mp="18crig3divgr9" />
      </div>
      <h4 className="text-[18px] font-semibold text-[#0F172A] mb-2" data-id="element-508" data-mp="1d642e079azdo">
        Request submitted
      </h4>
      <p className="text-[14px] text-[#475569] mb-2" data-id="element-509" data-mp="1i3vv2wfq6pix">
        Your {submittedSummary?.actionLabel} request for{' '}
        <strong data-id="element-510" data-mp="1n5la0n62jnca">{submittedSummary?.count}</strong>{' '}
        {submittedSummary?.count === 1 ? 'subscription' : 'subscriptions'} has
        been submitted.
      </p>
      <p className="text-[14px] text-[#475569]" data-id="element-511" data-mp="1w2vla8ivzch7">
        It's processing in the background, so you can carry on working or close
        this page. Track it on the <strong data-id="element-512" data-mp="1q5n3elhjh40">Bulk Update Jobs</strong> tab —
        that's also where any subscriptions that couldn't be updated will be
        listed.
      </p>
      <p className="text-[13px] text-[#64748B] mt-4" data-id="element-513" data-mp="3ey00nvq6cy5x">
        We'll email your team a summary when it finishes.
      </p>
    </div>;
  if (selectedIds.length === 0) return null;
  return <>
      <div className={`bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-center justify-between shadow-[0_1px_3px_rgba(15,23,42,0.08)] animate-in fade-in slide-in-from-top-2 duration-200 ${className}`} data-id="element-514" data-mp="3nqbgknnnx37n">
        <div className="flex items-center gap-4" data-id="element-515" data-mp="2w0yui7kvws0e">
          <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-516" data-mp="2r1lyoxc1calo">
            {selectedIds.length}{' '}
            {selectedIds.length === 1 ? 'subscription' : 'subscriptions'}{' '}
            selected
          </span>
          <button onClick={onClearSelection} className="text-[13px] text-[#64748B] hover:text-[#0F172A] transition-colors duration-200" data-id="element-517" data-mp="300za3vjpz991">
            Clear selection
          </button>
        </div>

        <div className="flex items-center gap-3" data-id="element-518" data-mp="farqtym7a2xc">
          <Button variant="outline" size="md" onClick={() => openAction('reschedule')} iconLeft={<CalendarIcon size={16} data-id="element-520" data-mp="nrip9i2uvdfy" />} data-id="element-519" data-mp="fe5tl7ys0e5b">
            Reschedule
          </Button>
          <Button variant="outline" size="md" onClick={() => openAction('status')} iconLeft={<Tag size={16} data-id="element-522" data-mp="dvny1ykkga1k" />} data-id="element-521" data-mp="rvyr989cm0pw">
            Update Status
          </Button>
        </div>
      </div>

      {/* Reschedule Modal */}
      <Modal isOpen={currentAction === 'reschedule'} onClose={() => setCurrentAction(null)} title="Reschedule Subscriptions" footer={<>
            <Button variant="ghost" onClick={() => setCurrentAction(null)} data-id="element-524" data-mp="1bi4rutu3c5ik">
              Cancel
            </Button>
            <Button disabled={!isPutBack && !newDate} onClick={handleConfirmAction} data-id="element-525" data-mp="wua0bgrtk772">
              Next
            </Button>
          </>} data-id="element-523" data-mp="itz8obioqoku">
        {renderRescheduleContent()}
      </Modal>

      {/* Swap Product Modal - 2-Step Process */}
      <ProductSwapModal isOpen={currentAction === 'swap'} onClose={() => setCurrentAction(null)} products={mockProducts} selectedProductId={selectedProduct} replacementProductId={replacementProduct} onSelectProduct={setSelectedProduct} onSelectReplacement={setReplacementProduct} onConfirm={handleProductSwapConfirm} data-id="element-526" data-mp="1pgut8lny0rcq" />

      {/* Update Status Modal */}
      <Modal isOpen={currentAction === 'status'} onClose={() => setCurrentAction(null)} title="Update Status" footer={<>
            <Button variant="ghost" onClick={() => setCurrentAction(null)} data-id="element-528" data-mp="3h1yx9ksa9qqj">
              Cancel
            </Button>
            <Button disabled={!newStatus} onClick={handleConfirmAction} data-id="element-529" data-mp="324oerrh83klb">
              Next
            </Button>
          </>} data-id="element-527" data-mp="3c4ysreb30w1j">
        {renderStatusContent()}
      </Modal>

      {/* Preview Modal */}
      <Modal isOpen={currentAction === 'preview'} onClose={() => setCurrentAction(null)} title="Review your update" footer={<>
            <Button variant="ghost" onClick={() => setCurrentAction(null)} data-id="element-531" data-mp="rdy8yy575wg5">
              Back
            </Button>
            <Button onClick={handleFinalUpdate} data-id="element-532" data-mp="wdko5d4l5mrl">Update</Button>
          </>} data-id="element-530" data-mp="2t75oa7kt8e21">
        {renderPreviewContent()}
      </Modal>

      {/* Submitted confirmation */}
      <Modal isOpen={currentAction === 'submitted'} onClose={finishSubmitted} title="Request submitted" footer={<>
            <Button variant="ghost" onClick={finishSubmitted} data-id="element-534" data-mp="1f99r2kyo6wek">
              Done
            </Button>
            <Button onClick={handleViewJobs} data-id="element-535" data-mp="1aitd8zqxt8ro">View Bulk Update Jobs</Button>
          </>} data-id="element-533" data-mp="16evg2ul6a819">
        {renderSubmittedContent()}
      </Modal>
    </>;
};